import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

interface UploadState {
  isUploading: boolean;
  progress: number;
  status: string;
  flatName?: string;
  error?: string;
}

interface UploadContextType {
  upload: UploadState;
  isDetailModalOpen: boolean;
  showToast: boolean;
  toastMessage: string;
  startUpload: (flatName: string) => void;
  updateProgress: (progress: number, status: string) => void;
  completeUpload: () => void;
  failUpload: (error: string) => void;
  clearUpload: () => void;
  openDetailModal: () => void;
  closeDetailModal: () => void;
  showUploadToast: (message: string) => void;
}

const defaultUploadState: UploadState = {
  isUploading: false,
  progress: 0,
  status: "",
  flatName: undefined,
  error: undefined,
};

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [upload, setUpload] = useState<UploadState>(defaultUploadState);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Warn user before leaving if upload is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (upload.isUploading) {
        e.preventDefault();
        e.returnValue =
          "Your video is still uploading. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [upload.isUploading]);

  const startUpload = useCallback((flatName: string) => {
    setUpload({
      isUploading: true,
      progress: 0,
      status: "Starting...",
      flatName,
      error: undefined,
    });
  }, []);

  const updateProgress = useCallback((progress: number, status: string) => {
    setUpload((prev) => ({
      ...prev,
      progress,
      status,
    }));
  }, []);

  const completeUpload = useCallback(() => {
    setUpload((prev) => ({
      ...prev,
      isUploading: false,
      progress: 100,
      status: "Complete!",
    }));
    // Auto-clear after 5 seconds
    setTimeout(() => {
      setUpload(defaultUploadState);
    }, 5000);
  }, []);

  const failUpload = useCallback((error: string) => {
    setUpload((prev) => ({
      ...prev,
      isUploading: false,
      error,
      status: "Failed",
    }));
  }, []);

  const clearUpload = useCallback(() => {
    setUpload(defaultUploadState);
  }, []);

  const openDetailModal = useCallback(() => {
    setIsDetailModalOpen(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
  }, []);

  const showUploadToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  }, []);

  return (
    <UploadContext.Provider
      value={{
        upload,
        isDetailModalOpen,
        showToast,
        toastMessage,
        startUpload,
        updateProgress,
        completeUpload,
        failUpload,
        clearUpload,
        openDetailModal,
        closeDetailModal,
        showUploadToast,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
